// Smart Views für Seitenleiste und Übersichtsseiten: eine gemeinsame Liste (GET /api/smart-views), Anheften/Lösen
// nur im CRM (PUT/DELETE /api/smart-views/{id}/pin), Trefferzahlen für die Übersicht (GET …/anzahl, nacheinander).
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { http } from "../api/client";
import { smartViewsApi, type SmartViewKurz } from "../api";
import { beimNutzerwechsel } from "./sitzung";

type Zustand = { views: SmartViewKurz[] | null; fehler: unknown; laedt: boolean };

const ANFANG: Zustand = { views: null, fehler: null, laedt: false };
let zustand: Zustand = ANFANG;
const hoerer = new Set<() => void>();
let laufend: Promise<void> | null = null;
/** Zählt Nutzerwechsel: Antworten aus einer früheren Sitzung werden verworfen. */
let sitzung = 0;

function setzen(neu: Partial<Zustand>) {
  zustand = { ...zustand, ...neu };
  hoerer.forEach((h) => h());
}

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => hoerer.delete(h);
}

export function smartViewsLaden(): Promise<void> {
  if (laufend) return laufend;
  const meine = sitzung;
  setzen({ laedt: true });
  const p: Promise<void> = smartViewsApi
    .liste()
    .then((r) => {
      if (meine === sitzung) setzen({ views: r.data, fehler: null });
    })
    .catch((e) => {
      if (meine === sitzung) setzen({ fehler: e });
    })
    .finally(() => {
      if (meine !== sitzung) return;
      laufend = null;
      setzen({ laedt: false });
    });
  laufend = p;
  return p;
}

/**
 * Beim Abmelden, Anmelden und Nutzerwechsel: Liste, Pins, Trefferzahlen und laufende Abfragen verwerfen,
 * damit nach einem Wechsel im selben Tab nie die Smart Views des vorherigen Nutzers stehen bleiben.
 */
export function smartViewsZuruecksetzen(): void {
  sitzung++;
  laufend = null;
  zahlen.clear();
  warteschlange.length = 0;
  laeuft.clear();
  zustand = ANFANG;
  hoerer.forEach((h) => h());
  zahlGeaendert();
}

/** Gemeinsame Liste aller sichtbaren Smart Views (eigene + geteilte). Lädt beim ersten Gebrauch und nach einem Zurücksetzen. */
export function useSmartViews(): Zustand & { neuLaden: () => Promise<void> } {
  const z = useSyncExternalStore(abonnieren, () => zustand);
  useEffect(() => {
    if (z.views === null && !z.fehler && !laufend) void smartViewsLaden();
  }, [z.views, z.fehler]);
  const neuLaden = useCallback(() => smartViewsLaden(), []);
  return { ...z, neuLaden };
}

/** In der Seitenleiste anheften (an = true) oder lösen. Wirkt nur im CRM, Close bleibt unverändert. */
export async function anheften(viewId: string, an: boolean): Promise<void> {
  // sofort anzeigen, dann mit der Antwort des Servers abgleichen
  if (zustand.views) {
    const max = Math.max(-1, ...zustand.views.map((v) => v.pin_position ?? -1));
    setzen({
      views: zustand.views.map((v) => (v.id === viewId ? { ...v, pinned: an, pin_position: an ? (v.pinned ? v.pin_position : max + 1) : null } : v)),
    });
  }
  const pfad = `/api/smart-views/${encodeURIComponent(viewId)}/pin`;
  const meine = sitzung;
  try {
    const r = an ? await http.put<{ data: SmartViewKurz[] }>(pfad) : await http.del<{ data: SmartViewKurz[] }>(pfad);
    if (meine === sitzung) setzen({ views: r.data });
  } catch (e) {
    if (meine === sitzung) void smartViewsLaden();
    throw e;
  }
}

/** Angeheftete Smart Views in Pin-Reihenfolge. */
export function angeheftete(views: SmartViewKurz[]): SmartViewKurz[] {
  return views.filter((v) => v.pinned).sort((a, b) => (a.pin_position ?? 0) - (b.pin_position ?? 0));
}

/** Smart Views, deren Name nur aus Strichen/Linien besteht, dienen in Close als Trenner. */
export function istTrenner(name: string): boolean {
  return /^[\s\-–—_=·.]{3,}$/.test(name);
}

// ---------------------------------------------------------------------------
// Trefferzahlen
// ---------------------------------------------------------------------------

export interface Anzahl {
  total: number | null;
  stand: string | null;
  fehler?: string;
}

const zahlen = new Map<string, Anzahl & { geholt: number }>();
const zahlHoerer = new Set<() => void>();
let zahlStand = 0;
const warteschlange: string[] = [];
const laeuft = new Set<string>();
const PARALLEL = 2;
const MERKEN_MS = 5 * 60_000;

function zahlGeaendert() {
  zahlStand++;
  zahlHoerer.forEach((h) => h());
}

function weiter() {
  while (laeuft.size < PARALLEL && warteschlange.length) {
    const id = warteschlange.shift()!;
    const meine = sitzung;
    laeuft.add(id);
    http
      .get<{ total: number | null; stand: string | null }>(`/api/smart-views/${encodeURIComponent(id)}/anzahl`)
      .then((r) => {
        if (meine === sitzung) zahlen.set(id, { ...r, geholt: Date.now() });
      })
      .catch((e: unknown) => {
        if (meine === sitzung) zahlen.set(id, { total: null, stand: null, fehler: e instanceof Error ? e.message : "Fehler", geholt: Date.now() });
      })
      .finally(() => {
        if (meine !== sitzung) return;
        laeuft.delete(id);
        zahlGeaendert();
        weiter();
      });
  }
}

/** Trefferzahlen für die angegebenen Views holen (höchstens zwei Anfragen gleichzeitig, 5 Min. gemerkt). */
export function zahlenAnfordern(ids: string[], erzwingen = false): void {
  const jetzt = Date.now();
  for (const id of ids) {
    const z = zahlen.get(id);
    if (!erzwingen && z && jetzt - z.geholt < MERKEN_MS) continue;
    if (warteschlange.includes(id) || laeuft.has(id)) continue;
    if (erzwingen) zahlen.delete(id);
    warteschlange.push(id);
  }
  weiter();
}

export function useZahlen(): (id: string) => Anzahl | undefined {
  useSyncExternalStore(
    (h) => {
      zahlHoerer.add(h);
      return () => zahlHoerer.delete(h);
    },
    () => zahlStand,
  );
  return (id) => zahlen.get(id);
}

/** Wird von der Smart-View-Seite gemeldet, wenn Close eine Trefferzahl geliefert hat. */
export function zahlMelden(id: string, total: number | null): void {
  if (total === null) return;
  const alt = zahlen.get(id);
  if (alt?.total === total) return;
  zahlen.set(id, { total, stand: new Date().toISOString(), geholt: Date.now() });
  zahlGeaendert();
}

beimNutzerwechsel(smartViewsZuruecksetzen);

/**
 * Anzeigename für die Seitenleiste: der eigene Vorname als Präfix („👤 Dilsar · Follow-UP · 🥇 Gold“,
 * „📅 Simon · Follow-UP · …“, „D1 · Dilsar · …“) wird weggelassen, damit der unterscheidende Teil sichtbar bleibt.
 * Ein anderes Emoji vor dem Namen bleibt stehen, das Personen-Emoji 👤 fällt mit weg. Der Name in Close bleibt unverändert.
 */
export function kurzName(name: string, vorname: string | null | undefined): string {
  const v = vorname?.trim();
  if (!v) return name;
  const teile = name.split(/\s+·\s+/);
  if (teile.length < 2) return name;
  const muster = new RegExp(`^(\\P{L}*?)\\s*${v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "iu");
  for (let i = 0; i < Math.min(2, teile.length - 1); i++) {
    const m = muster.exec(teile[i].trim());
    if (!m) continue;
    const praefix = m[1].replace(/👤️?/gu, "").trim();
    teile.splice(i, 1);
    if (praefix) teile[i] = `${praefix} ${teile[i]}`;
    return teile.join(" · ");
  }
  return name;
}
